import type { RouteLocationGeneric, RouteRecordRaw } from 'vue-router'
import { requirementRouteCandidates, toAnyPermission } from '@/config/businessRoutePermissions'
import { REQUIREMENT_PERMISSIONS } from '@/views/requirement/domain/permissions'
import { legacyTenantRedirect, maybeRouteName } from './tenantScoped'

function requirementDetailRedirect(withNames: boolean) {
  return (to: RouteLocationGeneric) => {
    const tenantCode = String(to.params.tenantCode || '')
    const path = withNames
      ? `/t/${encodeURIComponent(tenantCode)}/requirements/records`
      : '/requirements/records'

    return {
      path,
      query: {
        ...to.query,
        detail: String(to.params.id || ''),
      },
    }
  }
}

const createRequirementChildren = (withNames: boolean): RouteRecordRaw[] => [
  {
    path: '',
    redirect: 'records',
  },
  {
    path: 'records',
    ...maybeRouteName(withNames, 'requirement-records'),
    component: () => import('@/views/requirement/RequirementList.vue'),
    meta: { permission: REQUIREMENT_PERMISSIONS.record.view }
  },
  {
    path: 'tasks',
    ...maybeRouteName(withNames, 'requirement-tasks'),
    component: () => import('@/views/requirement/RequirementTaskList.vue'),
    meta: { permission: REQUIREMENT_PERMISSIONS.task.view }
  },
  {
    path: 'tasks/:taskId',
    ...maybeRouteName(withNames, 'requirement-task-detail'),
    component: () => import('@/views/requirement/RequirementTaskDetail.vue'),
    meta: { permission: REQUIREMENT_PERMISSIONS.task.view }
  },
  {
    path: 'bugs',
    ...maybeRouteName(withNames, 'requirement-bugs'),
    component: () => import('@/views/requirement/RequirementBugList.vue'),
    meta: { permission: REQUIREMENT_PERMISSIONS.bug.view }
  },
  {
    path: 'bugs/:code',
    ...maybeRouteName(withNames, 'requirement-bug-detail'),
    component: () => import('@/views/requirement/RequirementBugDetail.vue'),
    meta: { permission: REQUIREMENT_PERMISSIONS.bug.view }
  },
  {
    path: 'iterations',
    ...maybeRouteName(withNames, 'requirement-iterations'),
    component: () => import('@/views/requirement/RequirementIterationList.vue'),
    meta: { permission: REQUIREMENT_PERMISSIONS.iteration.view }
  },
  {
    path: 'iterations/:id',
    ...maybeRouteName(withNames, 'requirement-iteration-detail'),
    component: () => import('@/views/requirement/RequirementIterationList.vue'),
    meta: { permission: REQUIREMENT_PERMISSIONS.iteration.view }
  },
  {
    path: 'analytics',
    ...maybeRouteName(withNames, 'requirement-analytics'),
    component: () => import('@/views/requirement/RequirementAnalytics.vue'),
    meta: { permission: REQUIREMENT_PERMISSIONS.analytics.view }
  },

  {
    path: ':id',
    ...maybeRouteName(withNames, 'requirement-detail'),
    redirect: requirementDetailRedirect(withNames),
    meta: { permission: REQUIREMENT_PERMISSIONS.record.view }
  }
]

const createRequirementRoute = (path: string, withNames: boolean): RouteRecordRaw => ({
  path,
  component: () => import('@/views/requirement/RequirementLayout.vue'),
  ...(withNames ? {} : { beforeEnter: legacyTenantRedirect }),
  meta: {
    permission: toAnyPermission(requirementRouteCandidates),
    defaultRouteBasePath: withNames ? '/t/:tenantCode/requirements' : '/requirements',
    defaultRouteCandidates: requirementRouteCandidates,
    ...(!withNames ? { legacyTenantRedirect: true } : {}),
  },
  children: createRequirementChildren(withNames)
})

const requirementRoutes: RouteRecordRaw[] = [
  createRequirementRoute('t/:tenantCode/requirements', true),
  createRequirementRoute('requirements', false),
]

export default requirementRoutes
